import { isEmpty, isNil } from 'lodash';
// @ts-expect-error $TSFixMeError: Could not find a declaration file for module 'd3-selection'. '/home/pedro/projects/mews-js/node_modules/d3-selection/bui...
import { select } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { TimeUnit } from '@mews/enums';
import { Resource } from '@mews-commander/resources';
import { DateTimeService, lt, showErrorAlert, TimeUnitUtils } from '@mews/framework';
import { getChildTimeUnit, getParentTimeUnit, getUrlParameters } from '@mews-commander/utils';
import { ANIMATION_DELAY } from '@optimus-web/common';
import { ReservationResourceUse, ResourceUseCluster } from '@mews-commander/legacy-entities/src/ducks/resourcesTree';
import { LINE_HEIGHT, INITIAL_DESKTOP_OFFSET, INITIAL_MOBILE_OFFSET, CURSOR, DATE_LEGEND_PARTS } from '../constants';
import { isOtherService } from '../utils/services';
import { getColumnWidthByTimeUnit } from '../utils/timeUnit';
import { TimelineService } from '../timelineService';
import { CalendarEventExtended, CursorPosition, TimelineOptions, TimelineState } from '../types';
import { getResourceLegendWidth } from '../utils/drawUtils';
import { round } from '../utils/mathUtils';
import { getTimelineCanvas } from '../utils/dom';
import { NewReservationInfo } from '../../newReservation/components/NewReservationPanel/types';
import { TimelineUrlParamsKeys } from '../schemas/timelineUrlParamsSchema';
import Controls from './controls';
import Model from './model';
import Animator from './animator';
import { DateLegend, QuickScroll, SpaceLegend, ResourceUseCanvas, Tooltip } from './renderer';
import { TimelineFilterSchemaKeys, TimelineFilterValues } from './TimelineFilters/timelineFilterSchema';
import ResourceUsePlaceholder from './renderer/resourceUsePlaceholder';
import ConfirmedResourceUsePlaceholder from './renderer/confirmedResourceUsePlaceholder';

export default function Timeline(options: TimelineOptions) {
    const { companionExtent, timelineTouchScreenTest, isPercentageDensityValuesEnabled, isTimelineTouchMoveEnabled } =
        options;
    let redirectToUseId: $TSFixMe = null;

    const element = select('#' + options.target);
    const canvas = getTimelineCanvas();

    const offset = getTodayOffset(options.isMobile);
    const columnWidth = getColumnWidthByTimeUnit(options.timeUnit);

    const constXAxis = scaleLinear()
        .domain([offset, offset + 1])
        .range([0, columnWidth]);
    const constYAxis = scaleLinear().domain([0, 1]).range([0, LINE_HEIGHT]);

    const state: TimelineState = {
        ctx: canvas.getContext('2d') as CanvasRenderingContext2D,
        xAxis: constXAxis,
        yAxis: constYAxis,
        canvasWidth: 0,
        canvasHeight: 0,
        scale: 1.0,
        constXAxis,
        constYAxis,
        selectedResourceUse: null as ReservationResourceUse | null,
        highlightedSpaces: [],
        searchedSpace: null,
        viewport: {
            minX: 0,
            maxX: 0,
            minY: 0,
            maxY: 0,
        },
        initialOffset: offset,
        withResourceLegend: options.isMobile,
        serviceId: options.serviceId,
        timeUnit: options.timeUnit,
        timeUnitPrevious: null,
        isTimelineDraggingEnabled: !!options.isTimelineDraggingEnabled,
        filterFloorNumbers: [],
        filterFeatureIds: [],
        filterCategoryIds: [],
        filterUnassignedReservationsEnabled: false,
        mouse: null,
        timezone: options.timezone,
        startOffset: options.startOffset,
        endOffset: options.endOffset,
        serviceTimeUnit: options.serviceTimeUnit,
        maximumAvailableEditableHistoryIntervalInMilliseconds:
            options.maximumAvailableEditableHistoryIntervalInMilliseconds,
        newReservationPlaceholderLabel: options.newReservationPlaceholderLabel,
        newReservationEnabled: options.newReservationEnabled,
    };

    const dateLegend = DateLegend(
        TimelineService.indexWithTimeUnitToColor,
        TimelineService.localizeTimeUnitDate,
        TimelineService.indexToTimeUnitDateTime,
        TimelineService.indexToTimeUnitParentUnit
    );

    const quickScroll = QuickScroll();
    const spaceLegend = SpaceLegend(TimelineService.fromTextCache);
    const resourceUseCanvas = ResourceUseCanvas(
        TimelineService.fromTextCache,
        TimelineService.indexWithTimeUnitToColor,
        companionExtent
    );
    const tooltip = Tooltip();

    const model = Model(state, handleHeightChanged);

    const controls = Controls(
        element,
        state,
        tooltip,
        quickScroll,
        model,
        requestDraw,
        mouseClicked,
        mouseMoved,
        onViewPortChanged,
        moveEnded,
        resourceUseDragged,
        selectResourceUse,
        deselectResourceUse,
        options.canManageReservations,
        timelineTouchScreenTest,
        isPercentageDensityValuesEnabled,
        isTimelineTouchMoveEnabled
    );
    const animator = Animator(
        state,
        model,
        spaceLegend,
        dateLegend,
        resourceUseCanvas,
        options.canManageReservations ? ResourceUsePlaceholder() : undefined,
        options.canManageReservations ? ConfirmedResourceUsePlaceholder() : undefined,
        quickScroll
    );

    const { [TimelineUrlParamsKeys.Reservation]: reservation, [TimelineUrlParamsKeys.Block]: block } = getUrlParameters(
        new URL(window.location.href)
    );
    redirectToUseId = reservation || block;

    model.updateResources(options.resources);
    window.addEventListener('resize', onResize);
    onResize();
    moveEnded();

    if (options.confirmedResourceUsePlaceholder) {
        model.setConfirmedResourceUsePlaceholder(options.confirmedResourceUsePlaceholder);
    }

    return {
        ...controls,
        dispose() {
            TimelineService.clearCaches();
            controls.dispose();
            window.removeEventListener('resize', onResize);
        },
        findResourceUse,
        goToResource(searchToken: $TSFixMe) {
            const resource = model.findResourceByName(searchToken);
            if (!resource) {
                return false;
            }

            return controls.goToResource(resource);
        },
        goToDate(utcDateString: string, timeUnit: TimeUnit, isAnimationDisabled?: boolean) {
            const index = TimelineService.utcToTimeUnitIndex(utcDateString, options.timezone, timeUnit);
            controls.goToIndex(index, isAnimationDisabled);
        },
        updateModel(
            customers: $TSFixMe,
            resourceUses: $TSFixMe,
            resources: $TSFixMe,
            resourcesToHighlight: Resource[],
            events: CalendarEventExtended[] | undefined,
            selectedResourceUse: $TSFixMe,
            isMobile: $TSFixMe,
            isTimelineDraggingEnabled: $TSFixMe,
            filterValues: TimelineFilterValues,
            selectedDateTime: string | undefined,
            selectedDensity: number | string,
            selectedTimeUnit: TimeUnit,
            searchedSpace: Resource | null,
            serviceTimeUnit: TimeUnit,
            maximumAvailableEditableHistoryIntervalInMilliseconds: number,
            confirmedResourceUsePlaceholder: NewReservationInfo | null,
            startOffset: string,
            endOffset: string,
            newReservationEnabled: boolean
        ) {
            state.serviceId = filterValues[TimelineFilterSchemaKeys.ServiceId];
            state.timeUnitPrevious = state.timeUnit || selectedTimeUnit;
            state.timeUnit = selectedTimeUnit;
            state.serviceTimeUnit = serviceTimeUnit;
            state.isTimelineDraggingEnabled = isTimelineDraggingEnabled;
            state.filterFloorNumbers = filterValues[TimelineFilterSchemaKeys.FloorNumber] ?? [];
            state.filterFeatureIds = filterValues[TimelineFilterSchemaKeys.ResourceFeatureId] ?? [];
            state.filterCategoryIds = filterValues[TimelineFilterSchemaKeys.ResourceCategoryId] ?? [];
            state.filterUnassignedReservationsEnabled =
                filterValues[TimelineFilterSchemaKeys.UnassignedReservationsEnabled] ?? false;
            state.highlightedSpaces = resourcesToHighlight;
            state.searchedSpace = searchedSpace;
            state.maximumAvailableEditableHistoryIntervalInMilliseconds =
                maximumAvailableEditableHistoryIntervalInMilliseconds;
            state.startOffset = startOffset;
            state.endOffset = endOffset;
            state.newReservationEnabled = newReservationEnabled;

            model.updateResources(resources);
            model.updateCustomers(customers);
            model.updateResourceUses(resourceUses, resources.lookup);
            model.updateEvents(events);

            controls.changeDensity(selectedDensity);

            model.setConfirmedResourceUsePlaceholder(confirmedResourceUsePlaceholder ?? null);

            const found = selectedResourceUse && model.findResourceUseById(selectedResourceUse.id);
            if (found) {
                state.selectedResourceUse = model.findResourceUseById(selectedResourceUse.id) ?? null;
                // @ts-expect-error $TSFixMeError: Object is possibly 'null'.
                state.selectedResourceUse.displaceY = 0;
            } else {
                state.selectedResourceUse = null;
            }

            controls.forceRedraw();

            if (redirectToUseId && !isEmpty(resources.resources)) {
                const redirectedUse = model.findResourceUseById(redirectToUseId);
                if (redirectedUse) {
                    redirectToUseId = null;
                    options.handleSelectResourceUse(redirectedUse);
                    controls.goToUse(redirectedUse);
                }
            }

            if (!isMobile && !state.withResourceLegend) {
                state.withResourceLegend = true;
            }

            state.initialOffset = getTodayOffset(isMobile);

            controls.forceRedraw();

            // Reset timeline to 0 index when changing view through filter
            if (selectedTimeUnit !== state.timeUnitPrevious && !selectedDateTime) {
                this.goToIndex(0, true);
            }
        },
        toggleResourceLegend() {
            state.withResourceLegend = !state.withResourceLegend;
            controls.forceRedraw();
        },
        clearConfirmedResourceUsePlaceholder() {
            model.setConfirmedResourceUsePlaceholder(null);
        },
        refresh,
    };

    function getTodayOffset(isMobile: $TSFixMe) {
        return isMobile ? INITIAL_MOBILE_OFFSET : INITIAL_DESKTOP_OFFSET;
    }

    function handleHeightChanged(height: $TSFixMe, minHeight: $TSFixMe) {
        controls.updateZoom(height, minHeight);
    }

    function selectResourceUse(position: CursorPosition) {
        const resourceUse = model.getResourceUseAtPosition(position);
        if (resourceUse) {
            state.selectedResourceUse = resourceUse;
        }
        controls.forceRedraw();

        return !isNil(resourceUse);
    }

    function mouseClicked(position: CursorPosition) {
        state.highlightedSpaces = [];

        const resource = model.getResourceAtPosition(position);
        const resourceUse = model.getResourceUseAtPosition(position);
        const resourceUsePlaceholder = model.getResourceUsePlaceholder();
        const isOtherServiceUse = resourceUse?.serviceId && isOtherService(resourceUse.serviceId, state.serviceId);
        const resourceLegendWidth = getResourceLegendWidth(model, state.scale, state.withResourceLegend);
        const legendClick = dateLegend.clicked(position, resourceLegendWidth);
        const parentTimeUnit = getParentTimeUnit(state.timeUnit);
        const childTimeUnit = getChildTimeUnit(state.timeUnit);

        const clickedIndex = round(state.xAxis.invert(position.x));

        if (legendClick) {
            model.setResourceUsePlaceholder(null);
            model.setConfirmedResourceUsePlaceholder(null);
            const selectedParentTimeUnit =
                legendClick === DATE_LEGEND_PARTS.PARENT_UNIT && state.timeUnit !== parentTimeUnit
                    ? parentTimeUnit
                    : null;
            const selectedColumnTimeUnit =
                legendClick === DATE_LEGEND_PARTS.COLUMN_UNIT && state.timeUnit !== childTimeUnit
                    ? childTimeUnit
                    : null;
            const selectedTimeUnit = selectedParentTimeUnit || selectedColumnTimeUnit;

            if (!selectedTimeUnit) {
                return;
            }

            const dateTime = TimelineService.indexToTimeUnitDateTime(clickedIndex, state.timeUnit);

            const selectedTimeUnitUtc = DateTimeService.toUtc(dateTime);

            if (selectedTimeUnit) {
                options.handleChangeView({
                    [TimelineFilterSchemaKeys.ServiceId]: state.serviceId,
                    [TimelineFilterSchemaKeys.FloorNumber]: state.filterFloorNumbers,
                    [TimelineFilterSchemaKeys.ResourceFeatureId]: state.filterFeatureIds,
                    [TimelineFilterSchemaKeys.ResourceCategoryId]: state.filterCategoryIds,
                    [TimelineFilterSchemaKeys.UnassignedReservationsEnabled]: state.filterUnassignedReservationsEnabled,
                    timeUnit: selectedTimeUnit,
                    dateTime: selectedTimeUnitUtc.value,
                });

                window.setTimeout(() => {
                    if (clickedIndex === 0) {
                        return;
                    }

                    const timeUnitStartUtc = TimeUnitUtils.getTimeUnitStartUtc(
                        selectedTimeUnitUtc.value,
                        state.timezone,
                        TimeUnitUtils.timeUnitToDateTimeUnit(selectedTimeUnit)
                    );

                    const index = TimelineService.utcToTimeUnitIndex(
                        timeUnitStartUtc,
                        state.timezone,
                        selectedTimeUnit
                    );

                    controls.goToIndex(Math.round(index), true);
                });
            }
        }

        if (resource && state.withResourceLegend) {
            if (!resource.isSlot) {
                options.handleResourceDetail(resource.id);
            }
        } else if (resourceUse) {
            options.handleCloseNewReservationPanel();
            if (resourceUse.isMoveable && !isOtherServiceUse && state.isTimelineDraggingEnabled) {
                canvas.style.cursor = CURSOR.DRAG;
            }
            // TODO: not ideal typing but for now good enough
            if (!(resourceUse as unknown as ResourceUseCluster).resourceUseIds) {
                options.handleSelectResourceUse(resourceUse);
            }
        } else if (resourceUsePlaceholder) {
            const selectedResource = model.getResourceAtY(position);

            if (selectedResource) {
                const newReservationInfo: NewReservationInfo = {
                    startUtc: resourceUsePlaceholder.startUtc,
                    endUtc: resourceUsePlaceholder.endUtc,
                    resourceId: selectedResource.id,
                    resourceCategoryId: selectedResource.resourceCategoryId,
                };
                options.handleConfirmNewReservation(newReservationInfo);
                model.setConfirmedResourceUsePlaceholder(newReservationInfo);
                window.setTimeout(() => {
                    controls.goToIndex(clickedIndex, false);
                }, ANIMATION_DELAY.default);
            }
        } else {
            deselectResourceUse();
            model.setResourceUsePlaceholder(null);
        }

        controls.forceRedraw();
    }

    function deselectResourceUse() {
        options.handleDeselectResourceUse();
        state.selectedResourceUse = null;
    }

    function mouseMoved(position: CursorPosition) {
        const newResourceUseCanBeCreated =
            state.newReservationEnabled && state.timeUnit === state.serviceTimeUnit && options.canManageReservations;

        if (newResourceUseCanBeCreated) {
            state.mouse = position;
        }

        const resource = model.getResourceAtPosition(position);
        const resourceUse = model.getResourceUseAtPosition(position);
        const resourceUsePlaceholder = model.getResourceUsePlaceholder();

        const events = model.getEventsAtPosition(position);
        const isOtherServiceUse = resourceUse?.serviceId && isOtherService(resourceUse.serviceId, state.serviceId);
        const resourceLegendWidth = getResourceLegendWidth(model, state.scale, state.withResourceLegend);
        const childTimeUnit = getChildTimeUnit(state.timeUnit);
        const parentTimeUnit = getParentTimeUnit(state.timeUnit);

        if (resource || resourceUse || position.x <= resourceLegendWidth) {
            // remove placeholder if mouse is hovering over some element on timeline
            model.setResourceUsePlaceholder(null);
        }

        let cursorType = CURSOR.MOVE;
        if ((resource && !resource.isSlot) || resourceUse) {
            cursorType = CURSOR.POINTER;
        }

        if (
            resourceUse &&
            state.selectedResourceUse &&
            resourceUse.id === (state.selectedResourceUse as $TSFixMe).id &&
            resourceUse.isMoveable &&
            options.canManageReservations &&
            !isOtherServiceUse &&
            state.isTimelineDraggingEnabled
        ) {
            cursorType = CURSOR.DRAG;
        }

        if (quickScroll.collides(position)) {
            cursorType = CURSOR.DRAG;
        }

        if (dateLegend.collidesWithChildUnit(position, resourceLegendWidth)) {
            if (childTimeUnit !== state.timeUnit) {
                cursorType = CURSOR.POINTER;
            } else {
                cursorType = CURSOR.MOVE;
            }
        }

        if (dateLegend.collidesWithParentUnit(position, resourceLegendWidth)) {
            if (parentTimeUnit !== state.timeUnit) {
                cursorType = CURSOR.POINTER;
            } else {
                cursorType = CURSOR.MOVE;
            }
        }

        if (resourceUsePlaceholder) {
            cursorType = CURSOR.POINTER;
        }

        canvas.style.cursor = cursorType;

        if (dateLegend.collidesWithEvent(position, state.xAxis, state.timeUnit) && !isEmpty(events)) {
            tooltip.showEvents(position.x, events, options.timezone);
            return;
        }

        if (dateLegend.collidesWithParentUnit(position, resourceLegendWidth)) {
            if (parentTimeUnit === state.timeUnit) return;
            tooltip.showChangeViewToParent(position.x, position.y, state.timeUnit);
            return;
        }

        if (dateLegend.collidesWithChildUnit(position, resourceLegendWidth)) {
            if (childTimeUnit === state.timeUnit) return;
            tooltip.showChangeViewToChild(position.x, position.y, state.timeUnit);
            return;
        }

        if (resource && spaceLegend.collides(position)) {
            tooltip.showSpace(state.xAxis, state.yAxis, state.scale, resource);
            return;
        }

        if (resourceUse) {
            tooltip.showUse(state.xAxis, state.yAxis, state.viewport, resourceUse, state.serviceId, options.timezone);
            return;
        }

        if (newResourceUseCanBeCreated) {
            // rerender timeline due to resource use placeholder
            animator.requestDraw();
        }

        tooltip.hide();
    }

    function requestDraw() {
        tooltip.hide();
        // @ts-expect-error $TSFixMeError: Expected 3 arguments, but got 1.
        quickScroll.resize({
            canvasHeight: state.canvasHeight,
            scale: state.scale,
            timelineHeight: controls.getTimelineHeight(),
        });
        model.viewportChanged();
        animator.requestDraw();
    }

    function onViewPortChanged() {
        model.setResourceUsePlaceholder(null);
        changeViewport(state.canvasWidth, state.canvasHeight);
    }

    function refresh() {
        options.refresh(state.viewport.minX, state.viewport.maxX, state.viewport.minY, state.viewport.maxY, model);
    }

    function moveEnded() {
        options.onViewportChanged(
            state.viewport.minX,
            state.viewport.maxX,
            state.viewport.minY,
            state.viewport.maxY,
            model
        );
    }

    function onResize() {
        const width: number = element.node().offsetWidth;
        const height: number = window.innerHeight - element.node().getBoundingClientRect().top;

        const ratio = window.devicePixelRatio || 1;
        canvas.setAttribute('width', (width * ratio).toString());
        canvas.setAttribute('height', (height * ratio).toString());
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        (canvas.getContext('2d') as CanvasRenderingContext2D).setTransform(ratio, 0, 0, ratio, 0, 0);
        state.ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        state.canvasHeight = height;
        state.canvasWidth = width;

        onViewPortChanged();
        controls.handleResize(width, height);
    }

    function changeViewport(width: $TSFixMe, height: $TSFixMe) {
        state.viewport = {
            minX: state.xAxis.invert(0),
            minY: state.yAxis.invert(0),
            maxX: state.xAxis.invert(width),
            maxY: state.yAxis.invert(height),
        };
    }

    function findResourceUse(id: string, isReservation: boolean) {
        const use = model.findResourceUseById(id);
        if (isNil(use)) {
            redirectToUseId = id;
            if (isReservation) {
                options.handleFetchReservation(id);
            } else {
                options.handleFetchBlock(id);
            }
        } else {
            options.handleSelectResourceUse(use);
            controls.goToUse(use);
        }
    }

    function resourceUseDragged(resourceUse: $TSFixMe, resource: $TSFixMe, forceAssign = false) {
        model.setResourceUsePlaceholder(null);
        const { assignedResourceId: resourceUseAssignedId, id: resourceUseId } = resourceUse;

        if (isNil(resource)) {
            // @ts-expect-error $TSFixMeError: Object is possibly 'null'.
            state.selectedResourceUse.displaceY = 0;
            return;
        }

        const { id: resourceId, isSlot: isResourceSlot, parentId: resourceParentId, resourceCategoryId } = resource;
        const isAssigningToSameSlot = resourceUseAssignedId === resourceId;
        const shouldAssign =
            resource &&
            !isResourceSlot &&
            resourceCategoryId &&
            (forceAssign ||
                isNil(resourceUseAssignedId) ||
                (!isAssigningToSameSlot && resourceUseAssignedId !== resourceParentId));

        if (!resourceCategoryId) {
            showErrorAlert({ description: lt('AssignedResourceUseToResourceWithoutCategoryIdErrorMessage') });
        }

        if (shouldAssign) {
            options.handleAssignResource({ resourceUseId, resourceId });
        } else {
            // @ts-expect-error $TSFixMeError: Object is possibly 'null'.
            state.selectedResourceUse.displaceY = 0;
            controls.forceRedraw();
        }
    }
}
